import { useEffect, useRef, useState } from "react";
import { timerExpire, timerTick } from "../lib/haptic";

interface Props {
  /** Card id — re-keys the timer when card changes. */
  cardKey: string;
  durationSec: number;
}

/**
 * BIG centred game-style countdown for question / task cards. Big readable
 * numbers, color shift (accent → amber → red) as time drains, pulsing glow
 * during the last 25%, gentle shake in the last 5 seconds, and a
 * radial flare burst at 0. Soft cue only — does NOT auto-resolve the card,
 * the host still controls Done / Failed manually.
 */
export function SoftTimer({ cardKey, durationSec }: Props) {
  const [remaining, setRemaining] = useState(durationSec);
  const expiredRef = useRef(false);
  const [exploded, setExploded] = useState(false);

  useEffect(() => {
    setRemaining(durationSec);
    setExploded(false);
    expiredRef.current = false;
    // Track which "last N seconds" tick we have already fired so the host
    // feels exactly one buzz per second on the final 3-2-1 countdown,
    // independent of RAF jitter.
    let lastTickedSecond = -1;
    const start = performance.now();
    let raf = 0;
    function step(now: number) {
      const elapsed = (now - start) / 1000;
      const left = Math.max(0, durationSec - elapsed);
      setRemaining(left);
      // Fire timerTick at the boundary of seconds 3, 2, 1 (one shot each).
      const ceilLeft = Math.ceil(left);
      if (left > 0 && ceilLeft <= 3 && ceilLeft !== lastTickedSecond) {
        lastTickedSecond = ceilLeft;
        timerTick();
      }
      if (left <= 0) {
        if (!expiredRef.current) {
          expiredRef.current = true;
          setExploded(true);
          timerExpire();
          setTimeout(() => setExploded(false), 800);
        }
        return;
      }
      raf = requestAnimationFrame(step);
    }
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [cardKey, durationSec]);

  const pct = durationSec > 0 ? Math.max(0, remaining) / durationSec : 0;
  const seconds = Math.ceil(remaining);
  const tense = pct <= 0.25 && remaining > 0;
  const warning = pct <= 0.5 && pct > 0.25;
  const critical = remaining > 0 && remaining <= 5;
  const ringColor = tense ? "#fb7185" : warning ? "#fbbf24" : "var(--accent, #ffffff)";
  const ringGlow = tense ? "rgba(251, 113, 133, 0.85)" : warning ? "rgba(251, 191, 36, 0.7)" : "rgba(255, 255, 255, 0.6)";

  return (
    <div
      className={`relative w-28 h-28 sm:w-32 sm:h-32 mx-auto select-none ${critical ? "pp-timer-shake" : ""}`}
      style={{ filter: `drop-shadow(0 0 24px ${ringGlow})` }}
    >
      {remaining > 0 && (
        <div
          className="absolute inset-0 rounded-full"
          style={{
            border: `2px solid ${ringColor}`,
            opacity: 0.3,
            animation: `pp-timer-pulse ${tense ? "0.7s" : "1.4s"} ease-out infinite`,
          }}
        />
      )}
      {exploded && (
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(251,113,133,0.9) 0%, transparent 70%)",
            animation: "pp-timer-burst 700ms ease-out forwards",
            pointerEvents: "none",
          }}
        />
      )}
      <svg viewBox="0 0 100 100" className="absolute inset-0 -rotate-90">
        <circle
          cx="50"
          cy="50"
          r="44"
          stroke="white"
          strokeOpacity="0.15"
          strokeWidth="4"
          fill="none"
        />
        <circle
          cx="50"
          cy="50"
          r="44"
          stroke={ringColor}
          strokeWidth="6"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${2 * Math.PI * 44}`}
          strokeDashoffset={`${2 * Math.PI * 44 * (1 - pct)}`}
          style={{
            transition: "stroke-dashoffset 100ms linear, stroke 220ms ease-out",
            filter: `drop-shadow(0 0 6px ${ringGlow})`,
          }}
        />
      </svg>
      <div
        className={`absolute inset-0 flex items-center justify-center font-display font-black tabular-nums ${tense ? "animate-pickPulse" : ""}`}
        style={{
          color: tense ? "#fb7185" : "#fff",
          fontSize: 48,
          textShadow: `0 0 12px ${ringGlow}`,
        }}
      >
        {seconds}
      </div>
    </div>
  );
}
